import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Chip,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Divider
} from '@mui/joy';
import {
  Edit,
  Delete,
  MoreVert,
  Info,
  Warning,
  Error,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { Note, PRIORITY_COLORS, PRIORITY_DISPLAY_NAMES, VISIBILITY_DISPLAY_NAMES } from '../types/note';

interface NoteCardProps {
  note: Note;
  onEdit?: (note: Note) => void;
  onDelete?: (note: Note) => void;
  compact?: boolean;
}

const NoteCard: React.FC<NoteCardProps> = ({
  note,
  onEdit,
  onDelete,
  compact = false
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    handleMenuClose();
    onEdit?.(note);
  };

  const handleDelete = () => {
    handleMenuClose();
    onDelete?.(note);
  };

  const getPriorityIcon = () => {
    switch (note.priority) {
      case 'important':
        return <Warning sx={{ fontSize: 16 }} />;
      case 'urgent':
        return <Error sx={{ fontSize: 16 }} />;
      default:
        return <Info sx={{ fontSize: 16 }} />;
    }
  };

  const getVisibilityIcon = () => {
    return note.visibility === 'public' ? 
      <Visibility sx={{ fontSize: 16 }} /> : 
      <VisibilityOff sx={{ fontSize: 16 }} />;
  };

  const formatTimeAgo = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return 'Unknown time';
    }
  };

  if (compact) {
    return (
      <Card 
        variant="outlined" 
        size="sm"
        sx={{
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'background.level1'
          },
          borderLeft: `4px solid ${PRIORITY_COLORS[note.priority]}`
        }}
      >
        <CardContent sx={{ p: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {getPriorityIcon()}
              {note.title && (
                <Typography level="body-sm" fontWeight="md" noWrap>
                  {note.title}
                </Typography>
              )}
            </Box>
            <IconButton size="sm" onClick={handleMenuOpen}>
              <MoreVert />
            </IconButton>
          </Box>
          
          <Typography level="body-xs" color="neutral" noWrap>
            {note.content}
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <Chip
              size="sm"
              variant="soft"
              sx={{
                backgroundColor: PRIORITY_COLORS[note.priority],
                color: 'white',
                fontSize: '0.75rem'
              }}
            >
              {PRIORITY_DISPLAY_NAMES[note.priority]}
            </Chip>
            <Typography level="body-xs" color="neutral">
              {formatTimeAgo(note.created_at)}
            </Typography>
          </Box>
        </CardContent>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          {onEdit && (
            <MenuItem onClick={handleEdit}>
              <Edit sx={{ mr: 1 }} />
              Edit
            </MenuItem>
          )}
          {onDelete && (
            <>
              <Divider />
              <MenuItem onClick={handleDelete} color="danger">
                <Delete sx={{ mr: 1 }} />
                Delete
              </MenuItem>
            </>
          )}
        </Menu>
      </Card>
    );
  }

  return (
    <Card 
      variant="outlined"
      sx={{
        borderLeft: `4px solid ${PRIORITY_COLORS[note.priority]}`,
        '&:hover': {
          backgroundColor: 'background.level1'
        }
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
            {getPriorityIcon()}
            {note.title && (
              <Typography level="h4" component="h3" fontWeight="md">
                {note.title}
              </Typography>
            )}
          </Box>
          <IconButton onClick={handleMenuOpen}>
            <MoreVert />
          </IconButton>
        </Box>
        
        <Typography level="body-md" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
          {note.content}
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              size="sm"
              variant="soft"
              sx={{
                backgroundColor: PRIORITY_COLORS[note.priority],
                color: 'white'
              }}
            >
              {PRIORITY_DISPLAY_NAMES[note.priority]}
            </Chip>
            <Chip
              size="sm"
              variant="outlined"
              startDecorator={getVisibilityIcon()}
            >
              {VISIBILITY_DISPLAY_NAMES[note.visibility]}
            </Chip>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography level="body-xs" color="neutral">
              Created by {note.creator_name}
            </Typography>
            <Typography level="body-xs" color="neutral">
              {formatTimeAgo(note.created_at)}
            </Typography>
          </Box>
        </Box>
      </CardContent>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {onEdit && (
          <MenuItem onClick={handleEdit}>
            <Edit sx={{ mr: 1 }} />
            Edit
          </MenuItem>
        )}
        {onDelete && (
          <>
            <Divider />
            <MenuItem onClick={handleDelete} color="danger">
              <Delete sx={{ mr: 1 }} />
              Delete
            </MenuItem>
          </>
        )}
      </Menu>
    </Card>
  );
};

export default NoteCard; 